/**
 * MONSTER TOWN — THE TOWN. OWNED BY THE WORLD CREW (src/world/**).
 *
 * Registers the `town` scene, builds the whole static world into it, wires up
 * walking and the tappable landmarks, and keeps the draw-call budget honest.
 *
 * THE PLACE (see `layout.ts` for the numbers):
 *   a 24x24 plot the kid owns and builds on, a picket fence with four gates, a
 *   ring road with crossings and lamp posts around it, and — off each side of
 *   that road — the house, the town shop, the ice cream shop, the restaurant,
 *   a pond, cottages, and a tree line fading into hills.
 *
 * KEPT FROM THE SCAFFOLD (other crews reference these): `TOWN_SCENE_ID`,
 * `TOWN_GRID`, `installWorld(ctx)`, `townCenter()`.
 */

import * as THREE from 'three'
import { engine } from '../core/engine'
import { bus } from '../core/events'
import type { GridSpec } from '../core/grid'
import { hex } from '../core/palette'
import { registerSaveSection, save } from '../core/save'
import { world } from '../core/scene'
import type { SceneId, Vec3 } from '../core/types'
import { wallet } from '../core/economy'
import { app, type GameContext } from '../app/context'
import { place } from '../build/placer'
import { townPropDemo } from '../content/townProps'
import { registerAngle, registerDemo, registerSceneryReset, registerSpot, settle } from '../harness'
import { mergeStats } from './merge'
import { BUILDINGS, FRONT, POND, ROAD_IN, YARD_HALF } from './layout'
import { buildNavMask, findWalkPath, installNav, isTownWalkable, lineIsClear, navStats } from './nav'
import { worldToCanvasPx } from '../input/pick'
// The one sanctioned cross-crew call: `attachHouseEntrance` exists so the world
// crew can hand the interiors crew its real doorstep cell (see entrance.ts).
import { attachHouseEntrance } from '../interior/entrance'
import {
  hotspotIds,
  hotspotStand,
  installHotspots,
  lampsAreLit,
  sceneryTaps,
  updateHotspots,
} from './hotspots'
import { buildTown, skyTexture, type TownBuild } from './terrain'

export const TOWN_SCENE_ID = 'town' as SceneId

/** 24 x 24 tiles at 2 world units = a 48 x 48 unit plot. */
export const TOWN_GRID: GridSpec = {
  id: 'town',
  scale: 'exterior',
  width: 24,
  depth: 24,
  levels: 6,
}

interface TownSave {
  day: number
  lastSeenAt: number
  visits: number
}

let townState: TownSave = { day: 1, lastSeenAt: 0, visits: 0 }
let built: TownBuild | null = null

/** Scenery clusters + their world bounding spheres, for distance culling. */
interface LodCluster {
  object: THREE.Object3D
  cx: number
  cz: number
  radius: number
}
const clusters: LodCluster[] = []
/**
 * Beyond this the cluster cannot be on screen even at the widest zoom (the
 * orthographic box reaches ~55 world units from the camera target), so hiding
 * it skips both its culling test and its pass through the shadow map.
 */
const LOD_RADIUS = 58
let lodEnabled = true

function collectClusters(node: ReturnType<typeof world.register>): void {
  clusters.length = 0
  const box = new THREE.Box3()
  const sphere = new THREE.Sphere()
  for (const child of node.statics.children) {
    box.setFromObject(child)
    if (box.isEmpty()) continue
    box.getBoundingSphere(sphere)
    clusters.push({
      object: child,
      cx: sphere.center.x,
      cz: sphere.center.z,
      radius: sphere.radius,
    })
  }
}

function applyLod(target: Vec3): void {
  if (!lodEnabled) return
  for (const c of clusters) {
    const d = Math.hypot(target.x - c.cx, target.z - c.cz) - c.radius
    const want = d < LOD_RADIUS
    if (c.object.visible !== want) c.object.visible = want
  }
}

// ---------------------------------------------------------------------------
// install
// ---------------------------------------------------------------------------

export function installWorld(ctx: GameContext): void {
  const node = world.register({
    id: TOWN_SCENE_ID,
    kind: 'exterior',
    grid: TOWN_GRID,
    sky: true,
    devName: 'Town plot',
  })

  // Warm daylight instead of a flat blue wall. Costs nothing to draw.
  ctx.renderer.scene.background = skyTexture()

  /*
   * ONE COMMITTED KEY FOR THE WHOLE GAME (brief: "commit one palette across
   * world, UI and characters"). The default hemisphere bounced `grassDark`
   * green off the ground and `skyTop` cyan off the sky, which tinted every
   * vertical surface in the game olive and every floor cold. Warm sand bounce
   * plus a pale sky is the cosy-daylight key the palette was designed around,
   * and it is what makes the town and the rooms read as the same place.
   */
  ctx.renderer.setAmbience(hex('skyHorizon'), hex('sand'), 1.05)

  /*
   * THE SHADOW BOX COVERS THE WHOLE STREET, and stops chasing the monster.
   *
   * Round 2: "the pink ice-cream shop and the red house cast NO shadow on the
   * grass while the lamp posts, bench, signboard and tree next to them cast long
   * ones — the buildings read as stickers." The renderer's default is a 52-unit
   * shadow camera re-centred on the player every frame; the town is 48 units
   * across with its buildings on the far rim, so whichever building the kid was
   * not standing next to fell out of the frustum and cast nothing.
   *
   * Pinned at the plot centre with a 40-unit half-extent, every building on the
   * ring is inside the box from anywhere a kid can stand. Scene transitions
   * release the pin so an interior gets its tight box back.
   */
  ctx.renderer.pinSun({ x: 0, z: 0 }, 40)
  bus.on('scene:entered', (e) => {
    if (e.scene === TOWN_SCENE_ID) ctx.renderer.pinSun({ x: 0, z: 0 }, 40)
    else ctx.renderer.pinSun(null, 14)
  })

  /*
   * FOG STAYS OUT OF THE TOWN. The skyline paints its own aerial perspective
   * (see `scatterGreenery`), which is controllable at every zoom; leaving the
   * renderer's default 55..130 fog on top of it double-hazed the far half of the
   * street, because an orthographic camera measures fog depth from a plane 60
   * units behind the target and the town spans 40 units of that depth.
   */
  ctx.renderer.scene.fog = new THREE.Fog(hex('skyHorizon'), 96, 190)

  /*
   * COSY DAYLIGHT. The renderer's sun is fixed to the +X/+Z side, so every
   * north- and west-facing shop front — which is most of the street, because
   * the buildings all face the plot — sat in permanent gloom. These two lights
   * are parented to the town scene (so interiors are unaffected), cast no
   * shadows and add no draw calls; they just stop half the town looking damp.
   */
  const fill = new THREE.DirectionalLight(hex('sunWarm'), 0.55)
  fill.position.set(-16, 13, -12)
  fill.castShadow = false
  node.root.add(fill)
  node.root.add(fill.target)
  node.root.add(new THREE.AmbientLight(hex('sunWarm'), 0.16))

  built = buildTown(node)

  /*
   * THE KID'S FRONT DOOR IS THE KID'S HOUSE.
   *
   * The interiors crew ships `attachHouseEntrance()` precisely so the world crew
   * can say where the way in is once its real shell exists (see the comment on
   * that function). Until this call landed it defaulted to a cell two tiles
   * north of the plot centre and raised its own stand-in house front there — a
   * wall slab under a much wider floating pyramid roof, standing in the middle
   * of the kid's building canvas while the actual house sat unused across the
   * road. That was the round-1 blocker. One call moves the doorstep onto the
   * real house's step and the stand-in stops being built at all.
   */
  const home = built.buildings.find((b) => b.spec.id === 'house')
  if (home !== undefined) {
    const step = {
      x: home.door.x + (home.standAt.x - home.door.x) * 0.5,
      z: home.door.z + (home.standAt.z - home.door.z) * 0.5,
    }
    attachHouseEntrance(node.grid.worldToCell(step.x, step.z))
  }

  collectClusters(node)

  installNav(node.grid, TOWN_SCENE_ID, built.blockers)
  installHotspots(ctx, node.actors, built.landmarks, built.taps, built.lampGlow)

  // The snap grid is a BUILDING aid — showing it while a kid is just living in
  // the town makes the world look like a spreadsheet.
  const hint = built.gridHint
  hint.visible = app.mode === 'build'
  bus.on('app:modeChanged', (e) => {
    hint.visible = e.mode === 'build'
  })

  // Anything the kid builds changes where a monster can walk.
  let scenery = true
  const remask = (): void => {
    if (built === null) return
    buildNavMask(node.grid, TOWN_SCENE_ID, scenery ? built.blockers : [])
  }

  /**
   * "HERE IS YOUR EMPTY PLOT" — the state a critic loop could not capture.
   *
   * `clearTown()` used to empty the placement grids and nothing else, so the
   * house, the street, the crossings and the neighbours all stayed standing and
   * the very first thing a kid sees was unreviewable. The world crew owns this
   * scenery, so the world crew says how to take it away: hide the baked statics
   * (the ground and the sky stay — an empty plot is grass, not a void) and drop
   * the nav blockers with them, so the bare plot is actually walkable rather
   * than full of invisible buildings.
   *
   * `visible = false` rather than dispose: it is a review state, and it must go
   * back exactly as it was.
   *
   * THE PAINT GOES TOO. Hiding only `statics` left every road, kerb, crossing,
   * gate path, plaza and pond ring standing, because all of those are GROUND —
   * so a tool that asked for the empty plot still got a picture of the finished
   * town with the props taken off, and said so. `built.paint` is that layer;
   * the wild-grass backdrop and the snap grid stay, so the plot is grass with a
   * grid on it, which is what an empty plot actually is.
   */
  const paint = built.paint
  registerSceneryReset({
    clear: () => {
      scenery = false
      node.statics.visible = false
      paint.visible = false
      lodEnabled = false
      hint.visible = app.mode === 'build'
      remask()
    },
    restore: () => {
      scenery = true
      node.statics.visible = true
      paint.visible = true
      lodEnabled = true
      remask()
    },
  })
  bus.on('build:placed', remask)
  bus.on('build:removed', remask)
  bus.on('build:undo', remask)
  bus.on('save:loaded', remask)

  registerSaveSection<TownSave>(
    {
      id: 'town',
      version: 1,
      defaults: () => ({ day: 1, lastSeenAt: 0, visits: 0 }),
      serialize: () => ({ ...townState, lastSeenAt: Date.now() }),
      hydrate: (raw, c) => {
        if (!c.isRecord(raw)) return c.defaults
        return {
          day: Math.max(1, Math.round(c.num(raw['day'], 1))),
          lastSeenAt: Math.max(0, c.num(raw['lastSeenAt'], 0)),
          visits: Math.max(0, Math.round(c.num(raw['visits'], 0))),
        }
      },
    },
    (value) => {
      townState = { ...value }
      // A new day starts if the kid has been away six hours. Purely cosmetic —
      // nothing in this game is ever missed by not showing up (LAW #2).
      const away = Date.now() - townState.lastSeenAt
      if (townState.lastSeenAt > 0 && away > 6 * 3600_000) townState.day++
      townState.visits++
    },
  )

  /*
   * EVERY DOOR MUST LEAD SOMEWHERE THAT EXISTS.
   *
   * The ice cream shop — the brief's headline v1 venue, the one thing the kid
   * builds and then runs — carried `toScene: 'venue1'` for a whole round. No
   * such scene has ever been registered. `trigger()` in hotspots.ts guards with
   * `world.has(to)` precisely so a door still knocks, bounces and chimes while
   * its interior is being built, which is right — but that same guard turned a
   * typo into a door that behaved perfectly and simply never opened, on the one
   * building the whole venue crew exists to serve.
   *
   * The typo is fixed in layout.ts. THIS is the fix for the class: a dead door
   * id is now loud on boot and gated by `npm run drive`. Deferred to
   * `app:booted` because interiors and venues register their scenes after the
   * world crew installs.
   */
  bus.on('app:booted', () => {
    for (const d of doorReport()) {
      if (!d.ok) {
        console.error(
          `[world] DEAD DOOR: "${d.id}" opens into scene "${d.toScene}", which is not registered. ` +
            `Registered scenes: ${world.ids().join(', ')}`,
        )
      }
    }
  })

  engine.addSystem({
    id: 'world.town',
    order: 45,
    render: (_alpha, frameMs) => {
      updateHotspots(frameMs)
      applyLod(ctx.camera.target)
    },
  })

  world.setActiveImmediate(TOWN_SCENE_ID)
  installWorldDebug(ctx, node)

  // Harness registration is deferred one tick because `installHarness()` runs
  // later in boot and registers placeholder angles under the same names; ours
  // must land last. `settle()` in every tool guarantees this has run.
  setTimeout(() => registerHarnessViews(), 0)
}

/**
 * Every tappable landmark that claims to open a scene or a panel, and whether
 * that target actually resolves. Read by the boot check above, by
 * `window.__world.doors()` and by the `drive` gate.
 */
export function doorReport(): { id: string; toScene: string; ok: boolean }[] {
  if (built === null) return []
  const out: { id: string; toScene: string; ok: boolean }[] = []
  for (const l of built.landmarks) {
    if (l.toScene === undefined) continue
    out.push({ id: l.id, toScene: l.toScene, ok: world.has(l.toScene as SceneId) })
  }
  return out
}

/** Centre of the plot in world space — the default spawn / camera target. */
export function townCenter(): Vec3 {
  const node = world.get(TOWN_SCENE_ID)
  if (node === undefined) return { x: 0, y: 0, z: 0 }
  const g = node.grid
  return g.cellToWorld({ gx: Math.floor(g.spec.width / 2), gy: 0, gz: Math.floor(g.spec.depth / 2) })
}

/** In-world day counter. Cosmetic; there is no clock pressure anywhere. */
export function townDay(): number {
  return townState.day
}

/** Every landmark's standing spot, for other crews (NPC routes, venue queues). */
export function townLandmark(id: string): Vec3 | null {
  return hotspotStand(id)
}

// ---------------------------------------------------------------------------
// harness views
// ---------------------------------------------------------------------------

function buildingSpot(id: string): Vec3 {
  const b = built?.buildings.find((x) => x.spec.id === id)
  return b === undefined ? townCenter() : b.door
}

function registerHarnessViews(): void {
  const centre = townCenter()
  const house = buildingSpot('house')
  const shop = buildingSpot('shop')
  const ice = buildingSpot('iceCream')
  const pond: Vec3 = { x: POND.cx + POND.hx, y: 0, z: POND.cz }

  // Real framings for the aliases the scaffold registered.
  registerAngle({ name: 'house-exterior', sceneId: TOWN_SCENE_ID, target: house, rot: 0, zoom: 1 })
  registerAngle({
    name: 'venue',
    sceneId: TOWN_SCENE_ID,
    target: { x: 0, y: 0, z: FRONT + 1 },
    rot: 2,
    zoom: 2,
  })
  // New views the world crew adds.
  registerAngle({ name: 'town-wide', sceneId: TOWN_SCENE_ID, target: centre, rot: 1, zoom: 3 })
  registerAngle({
    name: 'street',
    sceneId: TOWN_SCENE_ID,
    target: { x: 0, y: 0, z: -ROAD_IN - 1 },
    rot: 0,
    zoom: 1,
  })
  registerAngle({ name: 'shop-front', sceneId: TOWN_SCENE_ID, target: shop, rot: 3, zoom: 1 })
  registerAngle({ name: 'pond', sceneId: TOWN_SCENE_ID, target: pond, rot: 0, zoom: 2 })
  registerAngle({ name: 'ice-cream', sceneId: TOWN_SCENE_ID, target: ice, rot: 2, zoom: 1 })

  // A garden of every town prop, so the art gets looked at in a screenshot.
  // `place()` is the build crew's public API (contract §14, anyone may call).
  registerDemo('townProps', async () => {
    const items = townPropDemo()
    for (const it of items) {
      if (!wallet.canPlace(it.defId)) {
        wallet.earnExact(200, 'debug')
        wallet.buy(it.defId)
      }
      place(TOWN_SCENE_ID, it.defId, it.anchor, 0, { silent: true })
    }
    await settle(2)
    return items.length
  })

  registerSpot('gate-north', TOWN_SCENE_ID, { x: 0, y: 0, z: -YARD_HALF + 2 })
  for (const spec of BUILDINGS) {
    const stand = hotspotStand(`door:${spec.id}`)
    if (stand !== null) registerSpot(`door-${spec.id}`, TOWN_SCENE_ID, stand)
  }
  const pondStand = hotspotStand('pond')
  if (pondStand !== null) registerSpot('pond', TOWN_SCENE_ID, pondStand)

  // One standing spot per family of tappable scenery, so a tool can put the
  // monster beside a lamp / bench / flower bed / tree / sign and prove the tap
  // reacts without waiting out a forty-unit walk first.
  for (const family of ['lamp:', 'bench:', 'bloom:', 'tree:', 'sign:', 'plaza']) {
    const hit = sceneryTaps().find((t) => t.id.startsWith(family))
    if (hit !== undefined) {
      registerSpot(`scenery-${family.replace(':', '')}`, TOWN_SCENE_ID, hit.standAt)
    }
  }
}

/** Harness/debug: how well the town is behaving for an older iPad. */
export function townStats(): {
  clusters: number
  visibleClusters: number
  merged: { meshesIn: number; meshesOut: number }
  nav: { cells: number; blocked: number; driving: boolean; queued: number }
  day: number
} {
  let visible = 0
  for (const c of clusters) if (c.object.visible) visible++
  return {
    clusters: clusters.length,
    visibleClusters: visible,
    merged: mergeStats(),
    nav: navStats(),
    day: townState.day,
  }
}

/** Autosave hook for anything the town changes on its own. */
export function requestTownSave(reason: string): void {
  save.request(reason)
}

// ---------------------------------------------------------------------------
// world debug surface
// ---------------------------------------------------------------------------

/**
 * `window.__world` — the world crew's own QA handle. Deliberately NOT part of
 * `HarnessApi` (that interface is frozen); this is additive and read-only, and
 * exists so routing can be asserted rather than eyeballed.
 */
export interface WorldDebugApi {
  walkable(x: number, z: number): boolean
  path(from: Vec3, to: Vec3): Vec3[] | null
  clear(from: Vec3, to: Vec3): boolean
  landmarks(): string[]
  stand(id: string): Vec3 | null
  /** Standing scenery a tap reaches: id, what to aim at, where to stand. */
  scenery(): Array<{ id: string; at: Vec3; standAt: Vec3 }>
  /** Have the town's lamps been switched on? */
  lampsLit(): boolean
  /** Every door that claims a scene, and whether that scene is registered. */
  doors(): { id: string; toScene: string; ok: boolean }[]
  stats(): ReturnType<typeof townStats>
  /** Where the monster is right now, for routing QA. */
  player(): Vec3
  /** World point -> canvas pixels, via the canvas rect. Never window.*. */
  screen(p: Vec3): { x: number; y: number }
  /** Perf attribution: how much of a frame is the static town? */
  hideStatics(on: boolean): void
  shadowCasters(): number
}

declare global {
  interface Window {
    __world?: WorldDebugApi
  }
}

function installWorldDebug(ctx: GameContext, node: ReturnType<typeof world.register>): void {
  let hidden = false
  window.__world = {
    walkable: (x, z) => isTownWalkable(x, z),
    path: (from, to) => findWalkPath(from, to),
    clear: (from, to) => lineIsClear(from, to),
    landmarks: () => hotspotIds(),
    stand: (id) => hotspotStand(id),
    scenery: () =>
      sceneryTaps().map((t) => ({ id: t.id, at: { ...t.at }, standAt: { ...t.standAt } })),
    lampsLit: () => lampsAreLit(),
    doors: () => doorReport(),
    stats: () => townStats(),
    player: () => ({ ...app.playerPos }),
    screen: (p) => worldToCanvasPx(ctx.camera.camera, ctx.canvas, p),
    hideStatics: (on) => {
      hidden = on
      node.statics.visible = !on
      node.ground.visible = !on
      lodEnabled = !hidden
    },
    shadowCasters: () => {
      let n = 0
      node.root.traverse((o) => {
        if ((o as THREE.Mesh).isMesh === true && o.castShadow) n++
      })
      return n
    },
  }
}
